/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import MsDyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { fireAnalyticsLink } from '../../Utilities/analytics/clickTrack';
import { htmltoText } from '../../Utilities/analytics/htmlToText';
import { firePromotionClick, firePromotionImpression } from '../../Utilities/analytics/promotionClickTrack';
import { navLinkKeywordRemoval } from '../../Utilities/plp-url-utils';
import { getAnalyticsConfig, getPromoAnalyticsConfig } from './analytics';
import { IContentBlockViewProps } from './mfrm-shop-by-department';

const ContentBlockView: React.FC<IContentBlockViewProps> = (props) => {
  const {
    contentBlockContainer,
    imageContainer,
    detailsContainer,
    title,
    text,
    image,
    eyebrowText,
    contentBlockAnchorTag,
    isPromotion,
    config
  } = props;

  const pagePath = MsDyn365.isBrowser
    ? window.location.href.split(location.host)[1].includes('?')
      ? window.location.href.split(location.host)[1].split('?')[0]
      : window.location.href.split(location.host)[1]
    : '';
  const friendlyName = contentBlockContainer.moduleProps.friendlyName;
  const paragraphText = config.paragraph ? htmltoText(config.paragraph.toString()).trim() : '';

  const analyticsConfig = getAnalyticsConfig(pagePath, friendlyName, config.tealiumEvent);
  const promoAnalyticsConfig = getPromoAnalyticsConfig(
    props.config.image,
    contentBlockAnchorTag,
    pagePath,
    paragraphText,
    config.tealiumPromotion
  );

  const handleAnalytics = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (MsDyn365.isBrowser) {
      event.preventDefault();
      fireAnalyticsLink({ ...analyticsConfig });
      if (isPromotion) {
        firePromotionClick({ ...promoAnalyticsConfig });
      }

      if (contentBlockAnchorTag?.href) {
        window.location.href = navLinkKeywordRemoval(contentBlockAnchorTag.href, props.context.request);
      }
    }
  };

  const [ref, inView] = useInView({ triggerOnce: true, rootMargin: '25px' });

  React.useEffect(() => {
    if (MsDyn365.isBrowser && isPromotion && inView) {
      firePromotionImpression({ ...promoAnalyticsConfig });
    }
  }, [inView]);

  const cypressData: string[] = ['mfrm-nsrp-sbd-item'];
  config.tealiumEvent?.event_action && cypressData.push(config.tealiumEvent?.event_action?.replace(/\W+/gm, '-'));

  return (
    <Module {...contentBlockContainer}>
      <Node
        {...contentBlockAnchorTag}
        href={navLinkKeywordRemoval(contentBlockAnchorTag?.href, props.context.request)}
        ref={ref}
        className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
        onClick={handleAnalytics}
        data-cy={cypressData.join('-')}
      >
        {eyebrowText && <div className={'ms-content-block__eyebrow-text'}>{eyebrowText}</div>}
        <Node {...imageContainer}>{image}</Node>
        <Node {...detailsContainer}>
          {title}
          {text}
        </Node>
      </Node>
    </Module >
  );
};

export default ContentBlockView;
